/**
 * The event behind a tasting ticket: when it starts, who hosts it, how many
 * seats it has.
 *
 * These facts are written into the SKU's `sku_tags` JSON in Strapi, next to the
 * `tasting_ticket` tag that already rides there (see `tasting-ticket.ts`).
 * `sku_tags` is a free-form JSON column that Strapi hands to the frontend
 * untouched via `populate=*`, so a new tasting is a Strapi edit rather than a
 * schema migration, a backend deploy and a frontend deploy.
 *
 * That is a deliberate interim. WEB-1365 wants date, venue and capacity as
 * first-class SKU fields; until they exist, this is the one place that knows
 * their shape, and the day they become real columns only `parseTastingEvent`
 * has to change.
 *
 * Everything here is optional on purpose. A ticket whose `sku_tags` nobody has
 * filled in still renders a product page — it just shows fewer rows. Nothing on
 * this page may throw on a half-populated SKU: the buy button has to survive
 * a typo in a JSON field an editor filled in by hand.
 *
 * Example:
 *
 * ```json
 * {
 *   "tags": ["tasting_ticket"],
 *   "event": {
 *     "startsAt": "2026-10-25T17:00:00+01:00",
 *     "durationMinutes": 120,
 *     "hostedBy": "Andrew Wallace",
 *     "capacity": 12,
 *     "bannerImageUrl": "https://dataspiritory.b-cdn.net/…/banner.jpg",
 *     "bannerHeadline": "Kilkerran Tour 2026",
 *     "bannerText": "The Kilkerran team travels from Campbeltown to Munich…",
 *     "lineupSkuSlugs": ["kilkerran-12-years-old", "…"]
 *   }
 * }
 * ```
 */
/**
 * A tasting as it appears on the `/tastings` overview.
 *
 * Flat and already localized, because the card is rendered from JSON that
 * crossed an API route — the browser never talks to Strapi directly (WEB-1403:
 * the response-format header turns every client call into a failed preflight).
 */
export type TastingCardData = {
  /** The ticket SKU's slug. The card links to `/tastings/{slug}`. */
  slug: string;
  title: string;
  description: string | null;
  imageUrl: string | null;
  /** Venue name and city, already joined — "Spiritory Shop, Munich". */
  venue: string | null;
  startsAt?: string;
  hostedBy: string | null;
  /** Size of the room. Not seats left — that is an ask count, and it lives in
   * the marketplace, not in Strapi. */
  capacity: number | null;
};

/** One FAQ entry, its answer already rendered from markdown to HTML. */
export type TastingFaqEntry = {
  question: string;
  answerHtml: string;
};

/**
 * The place a tasting happens, flattened out of the Strapi `Venue` record.
 *
 * A tasting is not always in our own shop — distilleries and partner bars host
 * them too, which is the whole reason this stopped being `getCollectionStore()`
 * (WEB-1409).
 */
export type TastingVenue = {
  name: string;
  streetAddress: string;
  postalCode: string;
  addressLocality: string;
  addressCountry: string;
  googleMapsCid?: string;
  imageUrl?: string;
  /** Already rendered to HTML. How to find the door, where to park. */
  arrivalNoteHtml?: string;
};

/** Single-line address, as printed on the location card. */
export const formatVenueAddress = (venue: TastingVenue): string =>
  `${venue.streetAddress}, ${venue.postalCode} ${venue.addressLocality}`;

/**
 * The venue's Google Maps listing.
 *
 * Addressed by CID rather than by address wherever we have one: a query for
 * `Plinganserstraße 6, 81369 München` resolves to the street and shows no
 * business at all, while the CID names the Business Profile — opening hours,
 * photos, reviews, and a Route button one tap away.
 *
 * The field is called `googleMapsCid`, but what editors actually paste into it
 * is whatever the Maps Share button gave them, which is usually a
 * `maps.app.goo.gl/…` short link rather than a numeric CID. Both work; a value
 * that is already a URL is used as it stands, because wrapping it in
 * `?cid=https://…` produces a Maps page for nothing.
 */
export const buildVenueMapUrl = (venue: TastingVenue): string => {
  const cid = venue.googleMapsCid?.trim();
  if (!cid) {
    return `https://www.google.com/maps?q=${encodeURIComponent(
      `${venue.name}, ${formatVenueAddress(venue)}`
    )}`;
  }

  return /^https?:\/\//i.test(cid) ? cid : `https://www.google.com/maps?cid=${cid}`;
};

/**
 * Source for the embedded map.
 *
 * The keyless embed does not take a CID, so the venue is named instead — the
 * name in front of the address is what makes Google drop a labelled pin on the
 * business rather than an anonymous one on the street.
 */
export const buildVenueEmbedUrl = (venue: TastingVenue, locale: string): string =>
  `https://maps.google.com/maps?q=${encodeURIComponent(
    `${venue.name}, ${formatVenueAddress(venue)}`
  )}&hl=${encodeURIComponent(locale)}&z=16&output=embed`;

export type TastingEvent = {
  /**
   * Start of the event as an ISO 8601 timestamp *with* offset, e.g.
   * `2026-10-25T17:00:00+01:00`.
   *
   * The offset is not optional in practice: a bare `2026-10-25T17:00` is read
   * as local time by the browser and as UTC by the server, which is how a
   * 17:00 tasting renders as 19:00 for a visitor in Athens and as 15:00 in the
   * SSR HTML a crawler is served. Written by hand in Strapi, so
   * `formatEventDate`/`formatEventTime` pin the display timezone as well.
   *
   * Which offset is the trap. Berlin is `+02:00` in summer and `+01:00` in
   * winter, and the changeover is the last Sunday of October — the end of the
   * tasting season, so autumn dates sit right on it. `+02:00` on a date already
   * past the switch is an hour late by the time it is formatted back into
   * Berlin time, and the page then quietly advertises 16:00 for a 17:00 event.
   * Check the offset against the date, not against the season it feels like.
   */
  startsAt?: string;
  durationMinutes?: number;
  /** Named person or team pouring, e.g. "Andrew Wallace". */
  hostedBy?: string;
  /**
   * Total seats.
   *
   * Not what the page counts down — that is the number of open asks, one per
   * seat, which is the only figure that shrinks when somebody buys. Capacity is
   * the headline ("12 spots") and the denominator; it is allowed to disagree
   * with the ask count while seats are being sold, and it does.
   */
  capacity?: number;
  /** Wide event banner above the gallery. Omitted → no banner. */
  bannerImageUrl?: string;
  /** Headline on the banner. Falls back to the SKU name. */
  bannerHeadline?: string;
  /** Standfirst under the banner headline. Omitted → nothing rendered. */
  bannerText?: string;
  /**
   * Narrower crop for the banner below the `nine` breakpoint, where the box is
   * 230px tall and full-bleed. Omitted → the desktop image is used there too.
   */
  bannerImageMobileUrl?: string;
  /** Running order of the evening, already rendered to HTML. */
  agendaHtml?: string;
  /**
   * Questions asked about this evening, from the Tasting record.
   *
   * Empty → `TastingQuestions` falls back to the eight generic ones in
   * `sku.json`. Parking, the entrance and check-in are properties of a
   * building, not of Spiritory, so a tasting held somewhere else answers them
   * differently or not at all.
   */
  faq?: TastingFaqEntry[];
  /**
   * Where the evening happens. `null` → no location block at all, rather than
   * the Munich shop's address under someone else's tasting.
   */
  venue?: TastingVenue | null;
  /**
   * Slugs of the bottles poured, in pouring order.
   *
   * Rendered as the same product cards the marketplace uses everywhere else, so
   * a guest can buy what they tasted. Slugs that no longer resolve are dropped
   * rather than rendered as gaps.
   */
  lineupSkuSlugs?: string[];
};

/**
 * A tasting as it appears on somebody else's page, in "Upcoming Tastings".
 *
 * Flattened on the server: the card needs a name, a picture, two lines of copy
 * and a date, and shipping the whole Strapi SKU into the HTML for that would
 * put four unused kilobytes per card into every tasting page's payload.
 */
export type UpcomingTasting = {
  slug: string;
  name: string;
  imageUrl: string | null;
  description: string | null;
  venue: string | null;
  startsAt?: string;
};

/**
 * The SKU this reads from, in whichever shape the caller holds it — the Strapi
 * record on the server, the DTO on the client. Only `sku_tags` is touched, and
 * only after it has been checked, so the parameter stays `unknown` rather than
 * naming one of the two and casting at every call site.
 */
type SkuLikeForTastingEvent = unknown;

const asString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined;

const asPositiveNumber = (value: unknown): number | undefined => {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
};

const asStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const entries = value.map(asString).filter((entry): entry is string => Boolean(entry));
  return entries.length > 0 ? entries : undefined;
};

/**
 * The event described by a SKU's `sku_tags`, or `null` if it describes none.
 *
 * Every field is validated rather than cast: this reads a JSON blob typed by
 * whoever last edited it in Strapi, and a number written as `"12"` or a
 * `lineupSkuSlugs` left as a comma-separated string must not take the page
 * down with it.
 */
export const parseTastingEvent = (sku: SkuLikeForTastingEvent): TastingEvent | null => {
  const skuTags = (sku as { sku_tags?: unknown } | null | undefined)?.sku_tags;
  const event = (skuTags as { event?: unknown } | null | undefined)?.event;
  if (!event || typeof event !== 'object' || Array.isArray(event)) return null;

  const raw = event as Record<string, unknown>;

  const parsed: TastingEvent = {
    startsAt: asString(raw.startsAt),
    durationMinutes: asPositiveNumber(raw.durationMinutes),
    hostedBy: asString(raw.hostedBy),
    capacity: asPositiveNumber(raw.capacity),
    bannerImageUrl: asString(raw.bannerImageUrl),
    bannerHeadline: asString(raw.bannerHeadline),
    bannerText: asString(raw.bannerText),
    lineupSkuSlugs: asStringArray(raw.lineupSkuSlugs),
  };

  const hasAnything = Object.values(parsed).some(value => value !== undefined);
  return hasAnything ? parsed : null;
};

/**
 * The shop's timezone, which is the one the event actually happens in.
 *
 * Both the date and the time are shown in it rather than in the visitor's, so
 * "25 October 2026, 17:00" means the same thing to a buyer in Munich, in
 * Madrid and to a crawler rendering on a UTC server. A tasting is a place you
 * physically stand in; there is no reader for whom their own timezone is the
 * useful answer.
 */
const EVENT_TIME_ZONE = 'Europe/Berlin';

/**
 * Bare `en` formats US-first — "October 25, 2026" and "05:00 PM" — for an
 * evening in Munich described everywhere else on this page as 25 October,
 * 17:00. `en-GB` is the English the rest of the audience reads: day before
 * month, and a clock the other four languages already use.
 */
const displayLocale = (locale: string): string =>
  locale.toLowerCase() === 'en' ? 'en-GB' : locale;

/**
 * 24-hour, in every language.
 *
 * Not a formatting preference: the venue, the staff and the door are on a
 * 24-hour clock, and "05:00 PM" on the ticket page against "17:00" on the
 * banner and in the confirmation mail is the kind of mismatch that puts
 * somebody outside a locked shop an hour early. `h23` rather than `hour12:
 * false`, which renders midnight as 24:00 in some locales.
 */
const TIME_FORMAT = { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' } as const;

const toDate = (startsAt?: string): Date | null => {
  if (!startsAt) return null;
  const date = new Date(startsAt);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** `25 October 2026`, in the visitor's language. */
export const formatEventDate = (startsAt: string | undefined, locale: string): string | null => {
  const date = toDate(startsAt);
  if (!date) return null;

  return new Intl.DateTimeFormat(displayLocale(locale), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: EVENT_TIME_ZONE,
  }).format(date);
};

/** `25 Oct 2026` — the short form the pills and cards use. */
export const formatEventDateShort = (
  startsAt: string | undefined,
  locale: string
): string | null => {
  const date = toDate(startsAt);
  if (!date) return null;

  return new Intl.DateTimeFormat(displayLocale(locale), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: EVENT_TIME_ZONE,
  }).format(date);
};

/** `17:00`, in the shop's timezone. */
export const formatEventTime = (startsAt: string | undefined, locale: string): string | null => {
  const date = toDate(startsAt);
  if (!date) return null;

  return new Intl.DateTimeFormat(displayLocale(locale), {
    ...TIME_FORMAT,
    timeZone: EVENT_TIME_ZONE,
  }).format(date);
};

/**
 * `2 h` / `90 min`, in the visitor's language where `Intl` supports it.
 *
 * `Intl.DurationFormat` is still missing in some of the browsers we serve, so a
 * plain unit format stands in — a duration is two numbers and a noun, and a
 * wrong-language "min" beats a thrown `TypeError` on the product page.
 */
export const formatEventDuration = (
  durationMinutes: number | undefined,
  locale: string
): string | null => {
  if (!durationMinutes) return null;

  const unit = durationMinutes % 60 === 0 ? 'hour' : 'minute';
  const value = unit === 'hour' ? durationMinutes / 60 : durationMinutes;

  try {
    return new Intl.NumberFormat(displayLocale(locale), {
      style: 'unit',
      unit,
      unitDisplay: 'long',
    }).format(value);
  } catch {
    return unit === 'hour' ? `${value} h` : `${value} min`;
  }
};

/** Whether the event has already happened. Undated events count as upcoming. */
export const isPastEvent = (event: TastingEvent | null, now: Date = new Date()): boolean => {
  const date = toDate(event?.startsAt);
  return date ? date.getTime() < now.getTime() : false;
};
